import type { FC, ReactNode } from 'react'

import {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import {
    FastForward,
    Heart,
    PenLine,
    RotateCcw
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'
import { LETTER } from '@/constants/birthday'

const TYPE_SPEED = 34

// The letter types itself out, as if he is writing it live.
const LetterSection: FC = (): ReactNode => {
    const paperRef = useRef<HTMLDivElement>(null)
    const inView = useInView(paperRef, { threshold: 0.3 })
    const reducedMotion = useReducedMotion()
    const characters = useMemo(() => Array.from(LETTER.body), [])
    const [count, setCount] = useState(0)
    const done = count >= characters.length
    const typed = characters.slice(0, count).join('')

    useEffect(() => {
        if (!inView || done) return

        if (reducedMotion) {
            setCount(characters.length)
            return
        }

        const character = characters[count]
        const delay = character === ' ' ? 18 : TYPE_SPEED
        const timer = window.setTimeout(() => setCount((value) => value + 1), delay)

        return () => window.clearTimeout(timer)
    }, [inView, done, reducedMotion, count, characters])

    return (
        <section id='letter' aria-labelledby='letter-title' className='relative overflow-hidden bg-gradient-to-b from-secondary/50 to-background py-24 sm:py-32'>
            <div className='mx-auto max-w-3xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        id='letter-title'
                        title={LETTER.title}
                        subtitle={LETTER.waiting}
                    />
                </Reveal>

                <div ref={paperRef} className='relative mt-14'>
                    <div aria-hidden className='absolute inset-0 translate-x-2 translate-y-3 rotate-2 rounded-[2rem] bg-primary/15' />

                    <article className='relative rounded-[2rem] border border-primary/10 bg-paper px-6 pb-10 pt-7 shadow-2xl shadow-wine/10 sm:px-12 sm:pt-9'>
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <span
                                aria-live='polite'
                                className='inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1.5 text-sm font-bold text-primary'
                            >
                                {done ? <Heart className='size-4 shrink-0 fill-current' /> : <PenLine className='size-4 shrink-0' />}
                                {done ? LETTER.done : LETTER.writing}
                            </span>

                            {done ? (
                                <button
                                    type='button'
                                    onClick={() => setCount(0)}
                                    className='inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-primary transition hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                                >
                                    <RotateCcw className='size-4' />
                                    {LETTER.replay}
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    onClick={() => setCount(characters.length)}
                                    className='inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-primary transition hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                                >
                                    <FastForward className='size-4' />
                                    {LETTER.skip}
                                </button>
                            )}
                        </div>

                        <div className='paper-lines relative mt-7 grid font-hand text-2xl leading-9 text-foreground sm:text-[1.7rem]'>
                            <p aria-hidden className='invisible col-start-1 row-start-1 whitespace-pre-wrap break-words'>
                                {LETTER.body}
                            </p>

                            <p aria-hidden className='col-start-1 row-start-1 whitespace-pre-wrap break-words'>
                                {typed}
                                {!done && <span className='ml-0.5 inline-block h-6 w-0.5 translate-y-1 animate-caret bg-primary motion-reduce:animate-none' />}
                            </p>

                            <p className='sr-only'>
                                {LETTER.body}
                            </p>
                        </div>

                        <p
                            className={cn(
                                'mt-8 text-right font-script text-4xl text-primary transition-opacity duration-700 motion-reduce:transition-none sm:text-5xl',
                                done ? 'opacity-100' : 'opacity-0'
                            )}
                        >
                            {LETTER.signature}
                        </p>
                    </article>
                </div>
            </div>
        </section>
    )
}

export default LetterSection
